import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Loader2, UserPlus, Mail, Ban, CheckCircle2, Trash2, ShieldCheck, Moon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_BADGE = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  invited: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  disabled: "bg-red-500/15 text-red-400 border-red-500/30",
};

const DAY = 86400000;

// Returns { text, dormant, never } describing the last sign-in.
function lastLogin(u) {
  if (!u.last_login_at) {
    return { text: u.status === "invited" ? "Not activated" : "Never signed in", never: true, dormant: u.status === "active" };
  }
  const then = new Date(u.last_login_at).getTime();
  const diff = Date.now() - then;
  const days = Math.floor(diff / DAY);
  let text;
  if (diff < 60000) text = "Just now";
  else if (diff < 3600000) text = `${Math.floor(diff / 60000)}m ago`;
  else if (diff < DAY) text = `${Math.floor(diff / 3600000)}h ago`;
  else if (days === 1) text = "Yesterday";
  else if (days < 30) text = `${days}d ago`;
  else text = new Date(u.last_login_at).toLocaleDateString();
  return { text, dormant: days >= 30, never: false };
}

export default function AdminPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setUsers([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    setError(""); setCreating(true);
    try {
      await api.post("/admin/users", { email, name });
      toast.success(`Invitation emailed to ${email}`);
      setName(""); setEmail("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const setStatus = async (u, status) => {
    setBusyId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/status`, { status });
      toast.success(status === "disabled" ? "User disabled" : "User re-enabled");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const resendInvite = async (u) => {
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/resend-invite`);
      toast.success(`Link re-sent to ${u.email}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    const u = toDelete;
    setToDelete(null);
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen w-screen bg-slate-950 text-slate-100 font-body canvas-dot-grid" data-testid="admin-page">
      <Toaster theme="dark" position="top-right" />
      <header className="flex items-center justify-between px-6 py-3 border-b border-slate-800 bg-slate-950/95 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center gold-glow">
            <ShieldCheck className="h-5 w-5 text-slate-950" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-lg font-semibold tracking-tight leading-none">Admin · User management</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Vaastu Just 4 U</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="back-to-app-btn" onClick={() => navigate("/")}
            className="border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-200 h-9">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to analyzer
          </Button>
          <Button variant="ghost" size="sm" data-testid="admin-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="text-red-300 hover:text-red-200 hover:bg-slate-800 h-9">
            Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 grid gap-8 lg:grid-cols-[340px_1fr]">
        {/* Create user */}
        <section className="bg-slate-900/70 border border-slate-800 rounded-xl p-6 h-fit">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="h-5 w-5 text-amber-400" />
            <h2 className="font-display text-lg font-semibold">Invite a user</h2>
          </div>
          <p className="text-sm text-slate-400 mb-5 leading-relaxed">
            Creates the account and emails a secure link so the person can set their own password. No password is ever sent by email.
          </p>
          <form onSubmit={createUser} className="space-y-4" data-testid="create-user-form">
            <div className="space-y-1.5">
              <Label htmlFor="cu-name" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Full name</Label>
              <Input id="cu-name" required data-testid="create-user-name"
                value={name} onChange={(e) => setName(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100 h-11" placeholder="Jane Doe" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cu-email" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Email</Label>
              <Input id="cu-email" type="email" required data-testid="create-user-email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100 h-11" placeholder="jane@example.com" />
            </div>
            {error && (
              <div data-testid="create-user-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" disabled={creating} data-testid="create-user-submit"
              className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" /> Create & send invite</>}
            </Button>
          </form>
        </section>

        {/* Users list */}
        <section className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">All users</h2>
            <span className="font-mono text-xs text-slate-500">{users ? `${users.length} total` : ""}</span>
          </div>
          {users === null ? (
            <div className="flex items-center gap-3 text-slate-400 p-6">
              <Loader2 className="h-4 w-4 animate-spin" /> <span className="font-mono text-xs uppercase tracking-widest">Loading…</span>
            </div>
          ) : (
            <div className="divide-y divide-slate-800/70" data-testid="admin-users-list">
              {users.map((u) => (
                <div key={u.id} data-testid="admin-user-row" className="flex items-center gap-3 px-5 py-3.5 hover:bg-slate-900/60">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-slate-100 truncate">{u.name || "—"}</span>
                      {u.role === "admin" && (
                        <Badge variant="outline" className="border-sky-500/40 bg-sky-500/10 text-sky-300 font-mono text-[10px]">ADMIN</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 truncate">{u.email}</div>
                  </div>
                  <Badge variant="outline" className={`font-mono text-[10px] capitalize ${STATUS_BADGE[u.status] || STATUS_BADGE.active}`}>
                    {u.status}
                  </Badge>

                  {(() => {
                    const ll = lastLogin(u);
                    return (
                      <div className="w-[140px] text-right hidden sm:block" data-testid={`last-login-${u.id}`}>
                        <div className={`text-xs font-mono ${ll.never ? "text-slate-500" : "text-slate-300"}`} title={u.last_login_at || "never"}>
                          {ll.text}
                        </div>
                        {ll.dormant && (
                          <span data-testid={`dormant-${u.id}`} className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-mono text-amber-400">
                            <Moon className="h-3 w-3" /> Dormant
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  {u.role === "admin" ? (
                    <span className="text-xs text-slate-600 font-mono w-[180px] text-right pr-1">protected</span>
                  ) : (
                    <div className="flex items-center gap-1 w-[180px] justify-end">
                      <IconAction title="Resend link" testid={`resend-${u.id}`} busy={busyId === u.id} onClick={() => resendInvite(u)}>
                        <Mail className="h-4 w-4" />
                      </IconAction>
                      {u.status === "disabled" ? (
                        <IconAction title="Re-enable" testid={`enable-${u.id}`} busy={busyId === u.id} onClick={() => setStatus(u, "active")}>
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        </IconAction>
                      ) : (
                        <IconAction title="Disable" testid={`disable-${u.id}`} busy={busyId === u.id} onClick={() => setStatus(u, "disabled")}>
                          <Ban className="h-4 w-4 text-amber-400" />
                        </IconAction>
                      )}
                      <IconAction title="Delete" testid={`delete-${u.id}`} busy={busyId === u.id} onClick={() => setToDelete(u)}>
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </IconAction>
                    </div>
                  )}
                </div>
              ))}
              {users.length === 0 && (
                <div className="p-6 text-center text-slate-500 text-sm">No users yet.</div>
              )}
            </div>
          )}
        </section>
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This permanently removes <span className="text-slate-200 font-medium">{toDelete?.email}</span> and all of their saved plans. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={doDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function IconAction({ children, title, onClick, busy, testid }) {
  return (
    <button type="button" title={title} data-testid={testid} onClick={onClick} disabled={busy}
      className="w-8 h-8 rounded-md flex items-center justify-center text-slate-300 hover:bg-slate-800 disabled:opacity-40 transition-colors">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </button>
  );
}
